import {
  Button,
  Flex,
  Popover,
  Input,
  Radio,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { getAppointmentsAction } from "../../../../../redux/services/appointments/appointmentsServices";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../constants/config/appConfig";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import useDebounce from "../../../../../hooks/useDebounce";
import {
  dateObjToDispViewDate,
  timeObjToDispViewTimeWithSuffix,
} from "../../../../../utils/utils";
import { colors } from "../../../../../constants/colors";
import Spinner from "../../../../common/spinner/Spinner";
import { ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import Actions from "../../../../../redux/actions";
import { baseRouteMaker, baseRouteMakerForSuperAdmin, routes } from "../../../../../constants/routeConsts";

const ServiceProviderDetailsAppointments = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  const { userSession } = useLocalStorage(appConfig.SESSION_USER_INFO);

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { appointments } = useSelector((state) => state.appointments);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: appointments?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* dialog dependencies */
  const [orderStatus, setOrderStatus] = useState(
    `ASSIGNED_TO_SERVICE_PROVIDER APPROVED_BY_SERVICE_PROVIDER`
  );
  /* dialog dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue } = useDebounce(
    searchValue,
    500
  );
  const {
    selectedServiceProvider: { selectedServiceProviderId, navigateBack },
  } = useSelector((state) => state.serviceProviders);
  /* filter dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      order_status: orderStatus?.split(" "),
      organization_id: selectedServiceProviderId,
    };
    dispatch(getAppointmentsAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, debouncedSearchValue, orderStatus]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("chats.orderID"),
        dataIndex: "order_number",
        key: "order_number",
      },
      {
        title: t("Modules.Appointments.orderDate"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{record?.created_at ? `${dateObjToDispViewDate(record?.created_at, "ddmmyyyy")} - ${timeObjToDispViewTimeWithSuffix(dayjs(record?.created_at).utc().local().format('ddd, MMM D, H:mm z'))}` : "-"}</p>
        ),
      },
      {
        title: t("Modules.Appointments.orderType"),
        dataIndex: "order_type",
        key: "order_type",
      },
      {
        title: t("Modules.Provider.serviceProviderName"),
        dataIndex: "name",
        render: (_, record) => <p>{record?.organization?.name ?? "-"}</p>,
      },
      {
        title: t("appointmentInfo.customerName"),
        dataIndex: "user",
        key: "user",
        render: (_, record) => (
          <p>
            {record?.user?.first_name || record?.user?.last_name
              ? `${record?.user?.first_name} ${record?.user?.last_name}`
              : "-"}
          </p>
        ),
      },
      {
        title: t("ProfileSetup.countryLabel"),
        dataIndex: "country",
        key: "country",
        render: (country) => country ? country?.emoji+" "+country?.name : "-",
      },
      {
        title: t("appointmentInfo.paymentStatus"),
        key: "is_payment_completed",
        dataIndex: "is_payment_completed",
        render: (_, { is_payment_completed }) => (
          <>
            <Tag
              bordered={false}
              style={{
                background: is_payment_completed
                  ? colors.lightTeal
                  : colors.lightPinkishRed,
                color: is_payment_completed
                  ? colors.darkTeal
                  : colors.darkMaroon,
              }}
            >
              {is_payment_completed ? t("Modules.Appointments.paid") : t("Modules.Appointments.due")}
            </Tag>
          </>
        ),
      },
      {
        title: t("revnue.revenue"),
        dataIndex: "total_amount",
        key: "total_amount",
        render: (_, record) => (
          <p>{record?.total_amount ? record?.total_amount?.toFixed(2) : "-"}</p>
        ),
      },
      {
        title: t("appointmentInfo.appointmentStatus"),
        key: "system_status",
        dataIndex: "system_status",
        render: (_, { system_status }) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case "COMPLETED":
                return { background: colors.lightTeal, color: colors.darkTeal };

              case "INCOMPLETE":
                return {
                  background: colors.lightPastelBlue,
                  color: colors.electricBlue,
                };

              case "ASSIGNED_TO_SERVICE_PROVIDER":
                return {
                  background: colors.darkGoldenRod,
                  color: colors.mango,
                };

              case "APPROVED_BY_SERVICE_PROVIDER":
                return {
                  background: colors.darkGoldenRod,
                  color: colors.mango,
                };

              case "REJECTED":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "REFUND_PROCESSED":
                return { background: colors.slatePurple, color: colors.indigo };

              case "REFUND_REQUESTED":
                return { background: "#ebe9e6", color: "#696969" };

              default:
                break;
            }
          };
          return (
            <>
              <Tag
                bordered={false}
                style={getColorBasedOnStatus(system_status)}
              >
                {[
                  "ASSIGNED_TO_SERVICE_PROVIDER",
                  "APPROVED_BY_SERVICE_PROVIDER",
                ].includes(system_status)
                  ? "RECEIVED"
                  : system_status}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("tabs.appointmentInfo"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleViewDetails = (e, row) => {
    dispatch(Actions.createAction(Actions.SELECTED_CUSTOMER_ID_FOR_APPOINTMENT_VIEW_DETAILS, {selectedCustomerIdForAppointmentViewDetails:""}))
    navigate(baseRouteMakerForSuperAdmin(routes.CUSTOMER_INFO_DETAILS_APPOINTMENTS_VIEW));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedCustomerIdForAppointmentViewDetails = row?._id;
      dispatch(Actions.createAction(Actions.SELECTED_CUSTOMER_ID_FOR_APPOINTMENT_VIEW_DETAILS, {selectedCustomerIdForAppointmentViewDetails}))
    }
  };


  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Flex justify="space-between">
          <Flex gap="1rem" align="center">
            <Button
              type="default"
              shape="circle"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(navigateBack)}
            />
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("Modules.customer.search")}
              onChange={(event) => setSearchValue(event.target.value)}
              style={{ width: "370px" }}
            />
          </Flex>
          <Radio.Group
            value={orderStatus}
            onChange={(event) => {
              setPagination({
                pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
                pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
              });
              setOrderStatus(event.target.value);
            }}
          >
            <Radio
              value={`ASSIGNED_TO_SERVICE_PROVIDER APPROVED_BY_SERVICE_PROVIDER`}
            >
              {t("appointmentInfo.recieved")}
            </Radio>
            <Radio value={"COMPLETED"}>{t("appointmentInfo.completed")}</Radio>
            <Radio value={"REJECTED"}>{t("appointmentInfo.rejected")}</Radio>
          </Radio.Group>
        </Flex>
        {loading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={appointments?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default ServiceProviderDetailsAppointments;
